import adminDoc from "./admin.md?raw";
import agentDetailDoc from "./agent-detail.md?raw";
import agentsDoc from "./agents.md?raw";
import approvalsDoc from "./approvals.md?raw";
import auditDoc from "./audit.md?raw";
import environmentDetailDoc from "./environment-detail.md?raw";
import environmentsDoc from "./environments.md?raw";
import jobDetailDoc from "./job-detail.md?raw";
import jobsDoc from "./jobs.md?raw";
import projectDetailDoc from "./project-detail.md?raw";
import projectsDoc from "./projects.md?raw";
import reportDetailDoc from "./report-detail.md?raw";
import reportsDoc from "./reports.md?raw";
import settingsDoc from "./settings.md?raw";
import taskDetailDoc from "./task-detail.md?raw";
import taskTemplatesDoc from "./task-templates.md?raw";
import tasksDoc from "./tasks.md?raw";
import workspaceOverviewDoc from "./workspace-overview.md?raw";
import workspacesDoc from "./workspaces.md?raw";

export type PageDoc = {
	/** One line shown under the page title. */
	description: string;
	/** Markdown shown in the help drawer. Omit to show the description alone. */
	doc?: string;
};

const WS = "/_authenticated/$orgSlug/workspaces/$workspaceId";

/**
 * Page documentation, keyed by TanStack route id (see `routeTree.gen.ts` —
 * index routes keep their trailing slash). Rendered by `PageHeader`; pages
 * without an entry render exactly as before.
 */
export const PAGE_DOCS: Record<string, PageDoc> = {
	"/_authenticated/$orgSlug/workspaces/": {
		description: "Team spaces. Everything operational lives inside one.",
		doc: workspacesDoc,
	},
	"/_authenticated/$orgSlug/admin": {
		description:
			"Organization-wide workspaces, users and audit trail. Admins only.",
		doc: adminDoc,
	},
	[`${WS}/`]: {
		description: "The state of this workspace, and what to set up next.",
		doc: workspaceOverviewDoc,
	},
	[`${WS}/settings/`]: {
		description: "Admin settings for this workspace.",
		doc: settingsDoc,
	},
	[`${WS}/projects/`]: {
		description: "Related work grouped together.",
		doc: projectsDoc,
	},
	[`${WS}/projects/$projectId`]: {
		description:
			"Instructions, reports, notes and deployments, this is the context agents work from.",
		doc: projectDetailDoc,
	},
	[`${WS}/tasks/`]: {
		description: "Create, list and manage tasks.",
		doc: tasksDoc,
	},
	[`${WS}/tasks/$taskId`]: {
		description: "What was asked, what the agent did, and what it produced.",
		doc: taskDetailDoc,
	},
	[`${WS}/task-templates/`]: {
		description: "Saved task definitions you can reuse.",
		doc: taskTemplatesDoc,
	},
	[`${WS}/agents/`]: {
		description: "The AI operators registered to this workspace.",
		doc: agentsDoc,
	},
	[`${WS}/agents/$agentId`]: {
		description: "Identity, endpoint, role and health for one agent.",
		doc: agentDetailDoc,
	},
	[`${WS}/environments/`]: {
		description:
			"Registered operations targets — the cloud accounts agents act against.",
		doc: environmentsDoc,
	},
	[`${WS}/environments/$environmentId`]: {
		description: "Provider coordinates, classification and connection details.",
		doc: environmentDetailDoc,
	},
	[`${WS}/approvals/`]: {
		description:
			"List of approvals. Agents can request approvals for tasks they are working on.",
		doc: approvalsDoc,
	},
	[`${WS}/audit/`]: {
		description: "Every action in this workspace, by whom and when.",
		doc: auditDoc,
	},
	[`${WS}/reports/`]: {
		description:
			"The durable record of documentation, incident, investigation, content, resources and links in one place.",
		doc: reportsDoc,
	},
	[`${WS}/reports/$reportId`]: {
		description:
			"Details, linked projects, attached resources and the written record.",
		doc: reportDetailDoc,
	},
	[`${WS}/jobs/`]: {
		description: "Recurring work on a schedule.",
		doc: jobsDoc,
	},
	[`${WS}/jobs/$jobId`]: {
		description: "Definition, schedule and run history for one job.",
		doc: jobDetailDoc,
	},
	[`${WS}/runbooks/`]: {
		description: "Not yet available.",
	},
};
